import { DataResult } from '../../utils/DataResult';
import {
  normalizeAssetPath,
  sanitizeExternalUrl,
  withIconVariant,
} from '../../utils/urls';
import { ResolveAssetUrlOptions } from '../api';
import { BaseApiService, toRequestError } from './http';

/**
 * Resolves and caches the asset base url used to build icon/asset URLs.
 */
export class AssetService extends BaseApiService {
  private assetBaseUrlPromise?: Promise<string | undefined>;

  private async fetchAssetBaseUrl(): Promise<string | undefined> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(`${baseUrl}/Config/odp`);

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<{ BaseUrl?: string }> | null = await response.json();

    return sanitizeExternalUrl(body?.data?.BaseUrl)?.replace(/\/+$/, '');
  }

  private async getAssetBaseUrl(): Promise<string | undefined> {
    if (!this.assetBaseUrlPromise) {
      this.assetBaseUrlPromise = this.fetchAssetBaseUrl().catch(
        () => undefined,
      );
    }

    return this.assetBaseUrlPromise;
  }

  async resolveAssetUrl(
    path: string,
    options: ResolveAssetUrlOptions = {},
  ): Promise<string | undefined> {
    if (/^[a-z][a-z0-9+.-]*:/i.test(path.trim())) {
      return sanitizeExternalUrl(path);
    }

    const assetPath = normalizeAssetPath(path);

    if (!assetPath) {
      return undefined;
    }

    const baseUrl = await this.getAssetBaseUrl();

    if (!baseUrl) {
      return path;
    }

    const { size, mode = 'Dark' } = options;
    const iconPath =
      size !== undefined ? withIconVariant(assetPath, size, mode) : assetPath;

    try {
      const assetUrl = new URL(`${baseUrl}${iconPath}`);

      return assetUrl.href.startsWith(baseUrl) ? assetUrl.href : undefined;
    } catch {
      return undefined;
    }
  }
}
